import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Spin, Table, Space, Select } from 'antd';
import {
  DollarOutlined,
  BarChartOutlined,
  RiseOutlined,
  FundOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, LineChart, Line, ComposedChart, ReferenceLine,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import PeriodToolbar from '../components/common/PeriodToolbar';
import api from '../libs/api';

const fmt = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(0)}`;
};

const pctFmt = (v) => (v != null ? `${v}%` : '-');

const plColumns = [
  { title: 'Entity', dataIndex: 'name', key: 'name', fixed: 'left', width: 160 },
  { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: fmt, align: 'right' },
  { title: 'COGS', dataIndex: 'cogs', key: 'cogs', render: fmt, align: 'right', responsive: ['md'] },
  { title: 'Gross Profit', dataIndex: 'grossProfit', key: 'grossProfit', render: fmt, align: 'right' },
  { title: 'OPEX', dataIndex: 'opex', key: 'opex', render: fmt, align: 'right', responsive: ['md'] },
  { title: 'EBITDA', dataIndex: 'ebitda', key: 'ebitda', render: fmt, align: 'right' },
  {
    title: 'NPAT', dataIndex: 'netProfit', key: 'netProfit', align: 'right',
    render: (v) => <span style={{ color: v >= 0 ? '#05A677' : '#FA5252', fontWeight: 600 }}>{fmt(v)}</span>,
  },
  { title: 'GP Margin', dataIndex: 'gpMargin', key: 'gpMargin', render: pctFmt, align: 'right', responsive: ['md'] },
  { title: 'EBITDA Margin', dataIndex: 'ebitdaMargin', key: 'ebitdaMargin', render: pctFmt, align: 'right', responsive: ['md'] },
];

export default function MonthlyFinance() {
  const [period, setPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Each fiscal year is a separate workbook — only offer years the backend
  // actually has a file configured for, so we never silently show the wrong
  // year's numbers via the fallback file.
  const [fiscalYears, setFiscalYears] = useState(null);
  // null = the whole group. Entity is the choice that belongs beside the period,
  // so it takes the slot the fiscal-year picker used to hold.
  const [entity, setEntity] = useState(null);

  useEffect(() => {
    api.get('/api/monthly-finance/fiscal-years')
      .then(res => setFiscalYears(res.data.data || null))
      .catch(() => setFiscalYears(null));
  }, []);

  const fetchData = useCallback(async (p) => {
    if (!p) return;
    setLoading(true);
    try {
      const res = await api.get('/api/monthly-finance/summary', { params: p });
      setData(res.data.data);
    } catch { /* handled by interceptor */ }
    setLoading(false);
  }, []);

  useEffect(() => { if (period) fetchData(period); }, [period, fetchData]);

  const allEntities = data?.entityMonthly || [];
  const entityTrend = data?.entityTrend || [];

  /**
   * Everything below reads one entity or the whole group, decided here once.
   *
   * The server sends both the group figures and the per-entity ones, so
   * narrowing to an entity is a filter rather than another twenty-five second
   * round trip through Google Drive.
   */
  const entities = entity ? allEntities.filter((e) => e.name === entity) : allEntities;
  const g = entity
    ? (() => {
      const row = allEntities.find((e) => e.name === entity);
      if (!row) return null;
      return {
        ...row,
        gpMargin: row.revenue ? ((row.grossProfit / row.revenue) * 100).toFixed(1) : null,
        ebitdaMargin: row.revenue ? ((row.ebitda / row.revenue) * 100).toFixed(1) : null,
      };
    })()
    : data?.groupTotals;

  const monthlyTrend = entity
    ? (() => {
      // Preserve the month order the server sent rather than re-deriving it.
      const seen = [];
      const byMonth = new Map();
      for (const r of entityTrend) {
        if (r.name !== entity) continue;
        if (!byMonth.has(r.month)) { byMonth.set(r.month, { month: r.month, revenue: 0, netProfit: 0 }); seen.push(r.month); }
        const acc = byMonth.get(r.month);
        acc.revenue += r.revenue;
        acc.netProfit += r.netProfit;
      }
      return seen.map((m) => byMonth.get(m));
    })()
    : (data?.monthlyTrend || []);

  const entityOptions = [
    { value: null, label: 'All entities' },
    ...allEntities.map((e) => ({ value: e.name, label: e.name })),
  ];
  const isMonthMode = period?.periodType === 'month';
  const c = data?.custom;

  /**
   * The resolved period, beside the controls that set it.
   *
   * It used to be a card of its own carrying the same four figures as the
   * scorecards directly beneath — the same numbers twice, which reads as two
   * facts. What the card had that the scorecards do not is what the range
   * actually covered, so that is what survives.
   */
  const periodNote = c && (
    <Space size={8} wrap style={{ fontSize: 12 }}>
      <span style={{ color: '#141d26' }}>
        {dayjs(c.from).format('MMM YYYY')} – {dayjs(c.to).format('MMM YYYY')}
      </span>
      <span style={{ color: '#8c8c8c' }}>
        {c.monthCount} month{c.monthCount === 1 ? '' : 's'}
        {c.fiscalYears.length > 1
          && ` · FY${c.fiscalYears.map((f) => `${String(f).slice(2)}/${String(f + 1).slice(2)}`).join(' + FY')}`}
      </span>
      {c.monthsWithData < c.monthCount && (
        <span style={{ color: '#FA5252' }}>
          {c.monthCount - c.monthsWithData} with no column in the workbook
        </span>
      )}
      {(c.missingFiscalYears || []).length > 0 && (
        <span style={{ color: '#FA5252' }}>
          no workbook for FY{c.missingFiscalYears.join(', FY')}
        </span>
      )}
    </Space>
  );

  // Bar chart data: entity Revenue vs NPAT (month mode only)
  const barData = entities
    .filter(e => e.revenue || e.netProfit)
    .map(e => ({
      name: e.name.length > 14 ? e.name.substring(0, 14) + '...' : e.name,
      Revenue: Math.round(e.revenue || 0),
      NPAT: Math.round(e.netProfit || 0),
    }));

  // Line chart data: group monthly trend (quarter/YTD mode)
  const trendData = monthlyTrend.map(m => ({
    name: m.month,
    Revenue: m.revenue,
    NPAT: m.netProfit,
  }));

  /**
   * The same months with a running total alongside each.
   *
   * Only built for a custom range, where the span is chosen and the question is
   * usually "where had we got to by March" rather than "what did March do".
   */
  const cumulativeData = (() => {
    let r = 0;
    let n = 0;
    return trendData.map((m) => {
      r += (m.Revenue || 0);
      n += (m.NPAT || 0);
      return { ...m, 'Revenue to date': r, 'NPAT to date': n };
    });
  })();

  // Compute margin percentages
  const withMargins = (row) => ({
    ...row,
    gpMargin: row.revenue ? ((row.grossProfit / row.revenue) * 100).toFixed(1) : null,
    ebitdaMargin: row.revenue ? ((row.ebitda / row.revenue) * 100).toFixed(1) : null,
  });

  // Table data with totals row
  const tableData = [
    ...entities.map((e, i) => withMargins({ ...e, key: i })),
    ...(g ? [withMargins({
      key: 'total',
      name: 'Group Total',
      revenue: g.revenue,
      cogs: g.cogs,
      grossProfit: g.grossProfit,
      opex: g.opex,
      ebitda: g.ebitda,
      netProfit: g.netProfit,
    })] : []),
  ];

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Monthly Finance</h1>
        <p>Group P&amp;L from monthly financial reports (Excel on Google Drive)</p>
      </div>
      <PeriodToolbar
        onChange={setPeriod}
        defaultPreviousMonth
        allowedFiscalYears={fiscalYears}
        hideComparison
        allowCustom
        hideFiscalYear
        leadingControl={(
          <Select
            value={entity}
            onChange={setEntity}
            options={entityOptions}
            style={{ width: 170 }}
            size="small"
            disabled={!allEntities.length}
          />
        )}
        trailingContent={periodNote}
      />
      <Spin spinning={loading}>
        {/* KPI Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="Group Revenue"
                value={g?.revenue}
                formatter={(v) => fmt(v)}
                prefix={<DollarOutlined style={{ color: '#54A2CC' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="Gross Profit"
                value={g?.grossProfit}
                formatter={(v) => fmt(v)}
                prefix={<RiseOutlined style={{ color: '#05A677' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="EBITDA"
                value={g?.ebitda}
                formatter={(v) => fmt(v)}
                prefix={<BarChartOutlined style={{ color: '#f5b759' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="NPAT"
                value={g?.netProfit}
                formatter={(v) => fmt(v)}
                prefix={<FundOutlined />}
                valueStyle={{ color: g?.netProfit >= 0 ? '#05A677' : '#FA5252' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Chart: Line trend for quarter/YTD, Bar comparison for month */}
        {isMonthMode ? (
          barData.length > 0 && (
            <Card style={{ marginTop: 16 }} title="Entity Performance">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={barData} margin={{ top: 5, right: 20, bottom: 60, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-30} textAnchor="end" fontSize={11} />
                  <YAxis tickFormatter={(v) => fmt(v)} fontSize={11} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                  <Bar dataKey="Revenue" fill="#54A2CC" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="NPAT" fill="#05A677" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )
        ) : (
          trendData.length > 0 && (
            c ? (
              /**
               * One frame per measure: the month as bars on the left axis, the
               * running total as a line on the right.
               *
               * Two scales in one frame is normally a thing to avoid, because it
               * lets a reader take the crossing point for a fact. It is safe
               * here for a specific reason — the line is the bars' own running
               * total, so the two can only ever move together and a crossing
               * carries no meaning to be misread. The axes are coloured to their
               * series so which figure a gridline belongs to is never a guess.
               */
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                {[
                  { key: 'Revenue', cumKey: 'Revenue to date', label: 'Revenue', colour: '#54A2CC', cum: '#1f6f96', zero: false },
                  { key: 'NPAT', cumKey: 'NPAT to date', label: 'NPAT', colour: '#05A677', cum: '#04795a', zero: true },
                ].map((m) => (
                  <Col xs={24} lg={12} key={m.key}>
                    <Card title={`${m.label} — month and running total`}>
                      <ResponsiveContainer width="100%" height={330}>
                        <ComposedChart data={cumulativeData} margin={{ top: 5, right: 8, bottom: 20, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="name" fontSize={10}
                            angle={-35} textAnchor="end" height={56} interval={0}
                          />
                          <YAxis
                            yAxisId="month"
                            tickFormatter={(v) => fmt(v)}
                            fontSize={11} width={64}
                            tick={{ fill: m.colour }}
                          />
                          <YAxis
                            yAxisId="cum" orientation="right"
                            tickFormatter={(v) => fmt(v)}
                            fontSize={11} width={64}
                            tick={{ fill: m.cum }}
                          />
                          <Tooltip formatter={(v) => fmt(v)} />
                          <Legend />
                          <Bar
                            yAxisId="month" dataKey={m.key} name={`${m.label} (month)`}
                            fill={m.colour} radius={[3, 3, 0, 0]} maxBarSize={28}
                          />
                          <Line
                            yAxisId="cum" type="monotone" dataKey={m.cumKey}
                            name={`${m.label} (running total)`}
                            stroke={m.cum} strokeWidth={2} dot={{ r: 3 }}
                          />
                          {/* Drawn on the running-total axis, since crossing into
                              cumulative loss is the event worth marking. */}
                          {m.zero && <ReferenceLine yAxisId="cum" y={0} stroke="#c9d1da" />}
                        </ComposedChart>
                      </ResponsiveContainer>
                      <div style={{ fontSize: 11, color: '#8c8c8c', textAlign: 'center' }}>
                        bars read on the left axis · line on the right
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
            <Card style={{ marginTop: 16 }} title="Group Monthly Trend">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={trendData} margin={{ top: 5, right: 20, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis tickFormatter={(v) => fmt(v)} fontSize={11} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="Revenue" stroke="#54A2CC" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="NPAT" stroke="#05A677" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            )
          )
        )}

        {/* P&L Breakdown Table */}
        {entities.length > 0 && (
          <Card style={{ marginTop: 16 }} title="P&L by Entity">
            <Table
              dataSource={tableData}
              columns={plColumns}
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
              rowClassName={(r) => r.key === 'total' ? 'table-row-bold' : ''}
            />
          </Card>
        )}
      </Spin>
    </PageLayout>
  );
}
